/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';
import styled from 'styled-components';
import { Merge } from 'ts-essentials';

import { Practitioner, ReceptionServiceWithPractitioners } from '@dreryk/egabinet-private-medical-service-frontend-api';
import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';
import { ServiceLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { FormContextValues, useFormController } from '@dreryk/react-common';
import { FormController, StyledFlex, StyledMargin, StyledMaxWidth } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { ReceptionServiceForm, ReceptionServiceFormBean } from '../shared/form/ReceptionServiceForm';
import { ReceptionServiceAdvancePayment } from '../shared/form/components/advancePayment/ReceptionServiceAdvancePayment';
import {
   mapReceptionServicePractitionersToBean,
   receptionServicePractitionerVisitor,
} from '../shared/form/components/practitioner/receptionServicePractitioner.utils';
import {
   mapReceptionServiceAdvancePaymentToBean,
   mapReceptionServicePriceToBean,
   receptionServicePaymentTypeVisitor,
   receptionServicePriceVisitor,
} from '../shared/form/components/price/receptionServicePrice.utils';
import {
   NotUniqueReceptionServiceNameErrorHandlerProp,
   useNotUniqueReceptionServiceNameErrorHandler,
} from '../shared/form/useNotUniqueReceptionServiceNameErrorHandler.hooks';
import {
   mapReceptionServiceLocationsToBean,
   serviceLocationControllerVisitor,
} from '../shared/form/components/serviceLocations/serviceLocations.utils';

import { ReceptionServiceSetAsFreeConfirmationEventInterceptor } from './ReceptionServiceSetAsFreeConfirmationEventInterceptor';

const StyledForColumnsSpan = styled.div`
   grid-column: 1 / span 4;
`;

StyledForColumnsSpan.displayName = 'StyledTwoColumnsSpan';

const StyledForm = styled.form`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   column-gap: 36px;
   row-gap: 24px;
`;

StyledForm.displayName = 'StyledForm';

export type ReceptionServiceUpdateFormBean = ReceptionServiceFormBean & { id: ReceptionServiceId };

export type ReceptionServiceUpdateFormController = FormContextValues<ReceptionServiceUpdateFormBean>;

declare type ReceptionServiceUpdateFormRenderProps = Merge<
   Pick<ReceptionServiceUpdateFormController, 'handleSubmit' | 'formState'>,
   NotUniqueReceptionServiceNameErrorHandlerProp
>;

declare type ReceptionServiceUpdateFormRenderer = (renderProps: ReceptionServiceUpdateFormRenderProps) => JSX.Element;

type ReceptionServiceUpdateFormProps = {
   readonly children: ReceptionServiceUpdateFormRenderer;
   readonly receptionService: ReceptionServiceWithPractitioners;
   readonly practitioners: Practitioner[];
   readonly serviceLocations: ServiceLocation[];
};

// eslint-disable-next-line max-lines-per-function
export const ReceptionServiceUpdateForm = (props: ReceptionServiceUpdateFormProps): JSX.Element => {
   const controller = useFormController<ReceptionServiceUpdateFormBean>(
      useMemo(() => {
         return {
            id: props.receptionService.id,
            name: props.receptionService.name,
            additionalInformation: props.receptionService.additionalInformation ?? null,
            color: props.receptionService.color?.asHexString() ?? null,
            estimatedDuration: props.receptionService.estimatedDuration.asMinutes(),
            videoConsultation: props.receptionService.deliveryMode === 'VIDEO_CONSULTATION',
            ...mapReceptionServicePriceToBean(props.receptionService),
            ...mapReceptionServiceAdvancePaymentToBean(props.receptionService),
            ...mapReceptionServicePractitionersToBean(props.receptionService, props.practitioners),
            ...mapReceptionServiceLocationsToBean(props.receptionService, props.serviceLocations),
         };
      }, [props.receptionService, props.practitioners, props.serviceLocations])
   );

   const bean = controller.watch();

   useUpdateEffect(() => {
      controller.trigger('price');
   }, [bean.freeChecked]);

   return (
      <>
         <FormController controller={controller.register('id')} />
         <ReceptionServiceForm>
            <ReceptionServiceForm.FourColumnsSpan>
               <ReceptionServiceForm.NameTextInput register={controller.register} />
            </ReceptionServiceForm.FourColumnsSpan>

            {protectFeature(
               'managingVideoConsultations',
               <ReceptionServiceForm.FourColumnsSpan>
                  <ReceptionServiceForm.VideoConsultationCheckbox register={controller.register} />
               </ReceptionServiceForm.FourColumnsSpan>
            )}

            <ReceptionServiceForm.FourColumnsSpan>
               <StyledFlex justifyContent={'space-between'} alignItems={'center'}>
                  <StyledMaxWidth maxWidth={133}>
                     <ReceptionServiceForm.EstimatedDuration register={controller.register} />
                  </StyledMaxWidth>
                  <StyledMargin right={132}>
                     <ReceptionServiceForm.ColorSelectorGroup register={controller.register} />
                  </StyledMargin>
               </StyledFlex>
            </ReceptionServiceForm.FourColumnsSpan>

            <ReceptionServiceForm.TwoColumnsSpan>
               <StyledFlex direction={'column'} gap={'8px'} flex={'1'}>
                  {receptionServicePriceVisitor(bean)({
                     withPrice: () => (
                        <StyledFlex direction={'row'} gap={'8px'}>
                           <StyledMaxWidth maxWidth={133}>
                              <ReceptionServiceForm.PriceMoneyInput register={controller.register} />
                           </StyledMaxWidth>
                           <StyledMargin top={36}>
                              <ReceptionServiceForm.PriceRangeCheckbox register={controller.register} />
                           </StyledMargin>
                        </StyledFlex>
                     ),
                     withPriceRange: () => (
                        <StyledFlex direction={'row'} gap={'8px'}>
                           <StyledMaxWidth maxWidth={302}>
                              <ReceptionServiceForm.PriceMoneyRangeInput register={controller.register} />
                           </StyledMaxWidth>
                           <StyledMargin top={36}>
                              <ReceptionServiceForm.PriceRangeCheckbox register={controller.register} />
                           </StyledMargin>
                        </StyledFlex>
                     ),
                  })}

                  {receptionServicePractitionerVisitor(props.practitioners)({
                     single: () => (
                        <ReceptionServiceForm.FreeToggle register={controller.register} checked={bean.freeChecked} />
                     ),
                     multiple: () => (
                        <ReceptionServiceSetAsFreeConfirmationEventInterceptor
                           receptionServiceName={props.receptionService.name}
                           freeChecked={bean.freeChecked}
                        >
                           <ReceptionServiceForm.FreeToggle register={controller.register} checked={bean.freeChecked} />
                        </ReceptionServiceSetAsFreeConfirmationEventInterceptor>
                     ),
                  })}

                  {receptionServicePaymentTypeVisitor(bean, {
                     notFree: () => <ReceptionServiceForm.AdvancePaymentToggle register={controller.register} />,
                  })}
               </StyledFlex>
            </ReceptionServiceForm.TwoColumnsSpan>

            <ReceptionServiceForm.FourColumnsSpan>
               {receptionServicePaymentTypeVisitor(bean, {
                  withAdvancePayment: () => (
                     <>
                        <ReceptionServiceForm.AdvancePaymentWarning />
                        <ReceptionServiceAdvancePayment
                           amount={
                              <ReceptionServiceForm.AdvancePaymentAmountMoneyInput
                                 register={controller.register}
                                 watch={controller.watch}
                              />
                           }
                        />
                     </>
                  ),
               })}

               <ReceptionServiceForm.AdditionalInformationTextarea register={controller.register} />

               {receptionServicePractitionerVisitor(props.practitioners)({
                  single: ({ practitioner }) => (
                     <ReceptionServiceForm.PractitionerTextView
                        register={controller.register}
                        defaultValue={practitioner}
                     />
                  ),
                  multiple: () => (
                     <ReceptionServiceForm.PractitionersInteractiveTable
                        register={controller.register}
                        control={controller.control}
                        readOnly={bean.freeChecked}
                        receptionServiceName={props.receptionService.name}
                        removeWithConfirmation
                        header={
                           <ReceptionServiceForm.PractitionersInteractiveTable.Selector
                              practitioners={props.practitioners}
                              register={controller.register}
                              control={controller.control}
                           />
                        }
                        bottomElement={
                           <ReceptionServiceForm.PractitionersInteractiveTable.Synchronization
                              control={controller.control}
                              getValue={controller.getValue}
                              setValue={controller.setValue}
                              trigger={controller.trigger}
                              watch={controller.watch}
                           />
                        }
                     />
                  ),
               })}

               {serviceLocationControllerVisitor(
                  controller,
                  props.serviceLocations
               )({
                  single: ({ controller, serviceLocation }) => (
                     <ReceptionServiceForm.ServiceLocationTextView
                        register={controller.register}
                        defaultValue={serviceLocation}
                     />
                  ),
                  multiple: ({ controller }) => (
                     <ReceptionServiceForm.ServiceLocationsInteractiveTable
                        register={controller.register}
                        header={
                           <ReceptionServiceForm.ServiceLocationsInteractiveTable.Selector
                              serviceLocations={props.serviceLocations}
                              register={controller.register}
                              control={controller.control}
                              setValue={controller.setValue}
                           />
                        }
                     />
                  ),
               })}
            </ReceptionServiceForm.FourColumnsSpan>
         </ReceptionServiceForm>

         {props.children({
            handleSubmit: controller.handleSubmit,
            formState: controller.formState,
            setNotUniqueReceptionServiceNameError: useNotUniqueReceptionServiceNameErrorHandler(controller.setError),
         })}
      </>
   );
};
/* eslint-enable */
